<script lang="ts">
    import { _ } from "svelte-i18n"
    import { onMount } from "svelte"
    import { grantAll, load, openConsentBanner, rejectAll, saveCustom } from "../../lib/consent"
    import { drainPurchaseQueue, loadGtag, resetTrackingState, trackPageView } from "../../lib/ga"
    import { initialLocale } from "../../i18n"
    import { staticFrontendLinks } from "../../store"
    import ContentBlock from "../pagebuilder/ContentBlock.svelte"
    import { getCachedEntry } from "../../api"
    import Icon from "../widgets/Icon.svelte"
    import { mdiChevronRight, mdiClose } from "@mdi/js"

    let visible = $state(false)
    let layer = $state<1 | 2>(1)
    let analytics = $state(false)
    let marketing = $state(false)
    let dialogEl: HTMLElement | null = $state(null)
    // Datenschutz-Panel: false = eingeklappt, true = nach rechts ausgefahren (Desktop)
    let detailsOpen = $state(false)
    let detailsLoaded = $state(false)
    let detailsToggle: HTMLElement | null = $state(null)
    let detailsRef: HTMLDetailsElement | null = $state(null)

    // Echte Datenschutzerklärung aus dem CMS (stabile Inhaltsseite, Pfad /de/service/datenschutz).
    // Kein Navigieren: nur die Content-Blöcke werden inline gerendert (ohne Sidebar, ohne
    // SEO-Head, ohne Link-Navigation), die Shop-Route bleibt stabil.
    let dataProtectionId = $derived($staticFrontendLinks?.["dataProtection"]?.[initialLocale])
    let storeFilled = $derived(Object.keys($staticFrontendLinks || {}).length > 0)
    let policyEntry: ContentEntry<"page"> | undefined = $state(undefined)
    let policyLoading = $state(false)

    async function ensurePolicy() {
        if (policyEntry || policyLoading || !dataProtectionId) return
        policyLoading = true
        try {
            policyEntry = (await getCachedEntry("content", { _id: dataProtectionId })) as
                | ContentEntry<"page">
                | undefined
        } catch {
            // stiller Fallback unten (Link auf stabile Policy-Seite)
        } finally {
            policyLoading = false
        }
    }

    // Nachladen, falls das Panel vor dem CMS-Store geöffnet wurde: sobald die
    // dataProtectionId eintrifft, Policy holen (kein stilles Leerlaufen).
    $effect(() => {
        if (detailsLoaded && dataProtectionId && !policyEntry && !policyLoading) {
            void ensurePolicy()
        }
    })

    // Body-Scroll sperren, solange der modale Dialog offen ist (wie SidebarOverlay).
    let prevBodyOverflow = $state("")
    $effect(() => {
        if (typeof document === "undefined") return
        if (visible) {
            if (prevBodyOverflow === "") prevBodyOverflow = document.body.style.overflow
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = prevBodyOverflow
            prevBodyOverflow = ""
        }
    })

    // Keine Navigation aus dem Policy-Text: alle Link-Klicks (Maus + Tastatur) abfangen.
    // Ausnahme: der Fallback-Link (öffnet die stabile Policy-Seite im neuen Tab).
    function blockPolicyNav(e: Event) {
        const anchor = (e.target as HTMLElement)?.closest?.("a")
        if (anchor && !anchor.hasAttribute("data-allow-nav")) {
            e.preventDefault()
        }
    }

    function refresh() {
        const rec = load()
        analytics = rec.choices.analytics
        marketing = rec.choices.marketing
    }

    function open(_layer: 1 | 2 = 1) {
        layer = _layer
        refresh()
        visible = true
        // Fokus auf den Dialog (Überschrift), nicht auf einen Button —
        // keine implizite Vorentscheidung für Screenreader/Tab-Nutzer.
        requestAnimationFrame(() => {
            dialogEl?.focus()
        })
    }

    function close() {
        visible = false
        layer = 1
    }

    function acceptAll() {
        grantAll()
        // Erst jetzt (mit Consent) gtag laden + genau einen page_view für diese Seite.
        loadGtag()
        trackPageView()
        // Wartende Käufe (Spät-Consent) jetzt nachfeuern.
        drainPurchaseQueue()
        close()
    }

    function reject() {
        rejectAll()
        // Widerruf räumt wartende Events + Pfad-Stempel weg (kein Nachsenden).
        resetTrackingState()
        close()
    }

    function save() {
        const rec = saveCustom({ analytics, marketing })
        if (rec.choices.analytics) {
            loadGtag()
            trackPageView()
            drainPurchaseQueue()
        } else {
            resetTrackingState()
        }
        close()
    }

    function toggleDetails(open: boolean) {
        detailsOpen = open
        if (open) {
            detailsLoaded = true
            void ensurePolicy()
        }
        // Nach dem Ausfahren Fokus in den Policy-Bereich (Screenreader folgen dem Inhalt).
        if (open) {
            requestAnimationFrame(() => {
                detailsToggle?.focus()
            })
        }
    }

    function closeDetails() {
        if (detailsRef) detailsRef.open = false
        toggleDetails(false)
        requestAnimationFrame(() => {
            detailsToggle?.focus()
        })
    }

    function onKeydown(e: KeyboardEvent) {
        if (e.key !== "Escape" || !visible) return
        // Ebene 1: keine Abwahl per ESC (keine Auswahl ohne Klick). Ebene 2: zurück.
        if (layer === 2) {
            e.preventDefault()
            layer = 1
        } else {
            e.preventDefault()
        }
    }

    onMount(() => {
        refresh()
        if (load().state === "pending") {
            visible = true
        }
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail
            open(detail?.layer === 2 ? 2 : 1)
        }
        window.addEventListener("renz:open-consent", handler)
        return () => window.removeEventListener("renz:open-consent", handler)
    })
</script>

<svelte:window onkeydown={onKeydown} />

{#if visible}
    <div class="renz-privacy-backdrop" aria-hidden="true"></div>
    <section
        class="renz-privacy-notice"
        class:expanded={detailsOpen}
        data-testid="renz-privacy-notice"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        tabindex="-1"
        bind:this={dialogEl}
    >
        <div class="renz-privacy-main">
            <div class="subline">{$_("consent.eyebrow")}</div>
            <h2 class="h2_nooffset" id="consent-title">{$_("consent.title")}</h2>
            <p class="renz-privacy-lead">{$_("consent.text")}</p>

            {#if layer === 1}
                <div class="renz-privacy-actions">
                    <button type="button" class="button secondary" data-testid="consent-accept-all" onclick={acceptAll}>
                        {$_("consent.accept-all")}
                    </button>
                    <button type="button" class="button secondary" data-testid="consent-reject" onclick={reject}>
                        {$_("consent.necessary-only")}
                    </button>
                    <!-- Vorerst deaktiviert (keine Marketing-Cookies im Einsatz):
                         Anpassen-Button + Ebene 2 bleiben für späteren Ausbau erhalten.
                    <button type="button" class="button secondary" data-testid="consent-customize" onclick={() => (layer = 2)}>
                        {$_("consent.customize")}
                    </button>
                    -->
                </div>
            {:else}
                <div class="renz-privacy-options">
                    <label class="renz-privacy-option">
                        <input type="checkbox" checked disabled />
                        <span>
                            <strong>{$_("consent.necessary-title")}</strong>
                            <small>{$_("consent.necessary-text")}</small>
                        </span>
                    </label>
                    <label class="renz-privacy-option">
                        <input
                            type="checkbox"
                            data-testid="consent-toggle-analytics"
                            bind:checked={analytics}
                        />
                        <span>
                            <strong>{$_("consent.analytics-title")}</strong>
                            <small>{$_("consent.analytics-text")}</small>
                        </span>
                    </label>
                    <!-- Vorerst deaktiviert (keine Marketing-Cookies im Einsatz):
                         Marketing-Toggle bleibt für späteren Ausbau erhalten.
                    <label class="renz-privacy-option">
                        <input
                            type="checkbox"
                            data-testid="consent-toggle-marketing"
                            bind:checked={marketing}
                        />
                        <span>
                            <strong>{$_("consent.marketing-title")}</strong>
                            <small>{$_("consent.marketing-text")}</small>
                        </span>
                    </label>
                    -->
                </div>
                <div class="renz-privacy-actions">
                    <button type="button" class="button secondary" data-testid="consent-save-custom" onclick={save}>
                        {$_("consent.save")}
                    </button>
                    <button type="button" class="button secondary" onclick={() => (layer = 1)}>
                        {$_("consent.back")}
                    </button>
                </div>
            {/if}
        </div>

        <details
            class="renz-privacy-details"
            data-testid="consent-details"
            bind:this={detailsRef}
            ontoggle={(e) => toggleDetails((e.currentTarget as HTMLDetailsElement).open)}
        >
            <summary data-testid="consent-details-summary" bind:this={detailsToggle} tabindex="-1">
                <span class="renz-privacy-details-title">{$_("consent.details-title")}</span>
                <span class="renz-privacy-details-chevron" aria-hidden="true">
                    <Icon path={mdiChevronRight} size="18px" />
                </span>
                <button
                    type="button"
                    class="renz-privacy-details-close"
                    data-testid="consent-details-close"
                    aria-label={$_("consent.details-close")}
                    title={$_("consent.details-close")}
                    onclick={(e) => {
                        // Im Summary würde der Klick nativ toggeln — verhindern und gezielt schließen.
                        e.preventDefault()
                        e.stopPropagation()
                        closeDetails()
                    }}
                >
                    <Icon path={mdiClose} size="22px" />
                </button>
            </summary>
            <div
                class="renz-privacy-details-body"
                data-testid="consent-details-body"
                onclickcapture={blockPolicyNav}
                onkeydowncapture={blockPolicyNav}
            >
                {#if policyEntry}
                    {#each policyEntry.blocks as block, idx}
                        <ContentBlock {block} contentEntry={policyEntry} />
                    {/each}
                {:else if detailsLoaded && storeFilled}
                    <p>{$_("consent.details-unavailable")}</p>
                    <p>
                        <a
                            href={`/${initialLocale}/service/datenschutz`}
                            target="_blank"
                            rel="noopener"
                            data-allow-nav="true"
                        >
                            {$_("consent.details-full")}
                        </a>
                    </p>
                {:else if detailsLoaded}
                    <p>{$_("consent.details-unavailable")}</p>
                {/if}
            </div>
        </details>
    </section>
{/if}

<style lang="less">
    @import "../../css/variables.less";

    .renz-privacy-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.35);
        z-index: 9998;
        animation: renz-privacy-fade 0.25s ease;
    }

    @keyframes renz-privacy-fade {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .renz-privacy-notice {
        position: fixed;
        z-index: 9999;
        left: 50%;
        top: 50%;
        right: auto;
        bottom: auto;
        transform: translate(-50%, -50%);
        width: min(560px, calc(100vw - 2rem));
        background: var(--surface, #fff);
        color: var(--on-surface, var(--comp--text--primary, #222));
        border-radius: var(--mainborder-radius, 12px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
        padding: 1.5rem 1.5rem 1.25rem;
        max-height: min(85vh, 640px);
        overflow-y: auto;
        animation: renz-privacy-pop 0.25s ease;
        transition:
            width 0.35s ease,
            transform 0.35s ease;

        @keyframes renz-privacy-pop {
            from {
                opacity: 0;
                transform: translate(-50%, -48%) scale(0.98);
            }
            to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
            }
        }

        .subline {
            color: var(--primary);
            font-weight: 700;
        }

        h2 {
            margin-bottom: 0.75rem;
        }

        .renz-privacy-lead {
            margin: 0 0 0.75rem 0;
            font-size: 0.9rem;
            line-height: 1.45;
        }

        // Desktop: Policy öffnet RECHTS neben dem Dialog, der kleine Dialog rutscht
        // nach links (Mitte bleibt stehen, Einwilligung links, Policy-Panel rechts).
        // Genau ein Scroller pro Spalte: Dialog selbst scrollt nie im Expanded-Modus.
        @media (min-width: 1200px) {
            &.expanded {
                width: min(1120px, calc(100vw - 3rem));
                height: min(94vh, 940px);
                max-height: none;
                overflow: hidden;
                display: grid;
                grid-template-columns: 430px minmax(0, 1fr);
                grid-template-rows: minmax(0, 1fr);
                gap: 0 1.5rem;
            }

            &.expanded > .renz-privacy-main {
                grid-column: 1;
                min-height: 0;
                overflow-y: auto;
                padding-right: 0.25rem;
            }

            &.expanded > .renz-privacy-details {
                grid-column: 2;
                min-height: 0;
                overflow: hidden;
            }

            &.expanded > .renz-privacy-details[open] summary {
                position: sticky;
                top: 0;
                background: var(--background, var(--grey-100, #f6f6f6));
                border-bottom: 1px solid var(--border-color-default, #e0e0e0);
                border-radius: var(--mainborder-radius, 8px) var(--mainborder-radius, 8px) 0 0;
                // Aufgeklappter Panel-Header (wie Renz-Closer-Leiste): reiner Titel
                // plus Schließen-Button — kein Strich.
                // [open]-Qualifier: sticht die Basis-[open]-Regel (gleiche Spec, später).
                text-decoration: none;
            }

            &.expanded > .renz-privacy-details[open] summary .renz-privacy-details-title {
                text-decoration: none;
            }

            &.expanded > .renz-privacy-details .renz-privacy-details-body {
                border-top: none;
                // Absolute Kappung statt Flex: <details> ist kein verlässlicher
                // Flex-Container (anonymer Inhalts-Slot). Sektionshöhe minus
                // Padding (~44px), Summary (~52px) und Ränder — Puffer bewusst
                // großzügig, damit nichts aus dem Kasten läuft.
                max-height: calc(min(94vh, 940px) - 110px);
                overflow-y: auto;
                overflow-x: clip;
            }
        }

        // Große Auflösung: mehr Platz nutzen.
        @media (min-width: 1600px) {
            &.expanded {
                width: min(1400px, calc(100vw - 4rem));
                grid-template-columns: 480px minmax(0, 1fr);
            }
        }

        @media (min-width: 1800px) {
            &.expanded {
                width: min(1600px, calc(100vw - 4rem));
                grid-template-columns: 520px minmax(0, 1fr);
            }
        }

        @media (min-width: 800px) and (max-width: 1199px) {
            &.expanded {
                width: min(860px, calc(100vw - 3rem));
            }
        }

        // Mobil: Policy als voller Screen mit Schließen-Button (kein Bottom-Sheet-Gefummel).
        // Die Sektion selbst wird zum Vollbild (ohne :has — das schluckt der Compiler),
        // die Details füllen sie. Kein transform mehr nötig.
        @media (max-width: 799px) {
            &.expanded {
                inset: 0;
                transform: none;
                width: auto;
                max-height: none;
                border-radius: 0;
                padding: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
            }

            &.expanded > .renz-privacy-main {
                display: none;
            }
            &.expanded > .renz-privacy-details {
                flex: 1;
                min-height: 0;
                margin: 0;
                border: none;
                border-radius: 0;
                background: var(--surface, #fff);
                overflow: hidden;
                max-height: none;
            }
            &.expanded > .renz-privacy-details[open] summary {
                position: sticky;
                top: 0;
                z-index: 1;
                min-height: 60px;
                padding: 0.75rem 1rem;
                background: var(--background, #fff);
                border-bottom: 1px solid var(--border-color-default, #e0e0e0);
                font-size: 1rem;
                // Vollbild-Header ist Navigationsleiste, kein Aufklapp-Link:
                // keine Unterstreichung (Close-Icon genügt).
                // [open]-Qualifier: sticht die Basis-[open]-Regel (gleiche Spec, später).
                text-decoration: none;
            }

            &.expanded > .renz-privacy-details[open] summary .renz-privacy-details-title {
                text-decoration: none;
            }

            &.expanded > .renz-privacy-details .renz-privacy-details-body {
                // Exakt füllen: Screen minus 60px Header — kein Leerraum unten.
                max-height: calc(100vh - 60px);
                max-height: calc(100dvh - 60px);
                overflow-y: auto;
                overflow-x: clip;
                border-top: none;
                padding: 1rem 1rem 2rem;
                font-size: 0.95rem;
            }

            &.expanded > .renz-privacy-details .renz-privacy-details-close {
                display: flex;
            }
        }

        .renz-privacy-actions {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 0.75rem;

            .button {
                width: 100%;
            }
        }

        .renz-privacy-options {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-bottom: 0.25rem;
        }

        .renz-privacy-option {
            display: flex;
            gap: 0.6rem;
            align-items: flex-start;
            font-size: 0.9rem;

            input {
                margin-top: 0.2rem;
                width: 1.1rem;
                height: 1.1rem;
                accent-color: var(--primary);
            }

            span {
                display: flex;
                flex-direction: column;

                small {
                    color: var(--comp--text--secondary, #666);
                }
            }
        }

        .renz-privacy-details {
            position: relative;
            margin: 0.75rem 0 0.75rem 0;
            border: 1px solid var(--border-color-default, #e0e0e0);
            border-radius: var(--mainborder-radius, 8px);
            background: var(--background, var(--grey-100, #f6f6f6));
            font-size: 0.85rem;

            summary {
                cursor: pointer;
                padding: 0.6rem 0.75rem;
                font-weight: 700;
                text-decoration: underline;
                text-underline-offset: 3px;
                list-style: none;
                display: flex;
                align-items: center;
                gap: 0.5rem;

                &::-webkit-details-marker {
                    display: none;
                }

                &:focus-visible {
                    outline: 3px solid var(--primary);
                    outline-offset: 2px;
                }

                .renz-privacy-details-title {
                    text-decoration: underline;
                    text-underline-offset: 3px;
                }

                // Dezenter Pfeil rechts (statt rotem Plus): zeigt Öffnen an,
                // bei geöffnetem Panel übernimmt der Schließen-Button.
                .renz-privacy-details-chevron {
                    margin-left: auto;
                    flex: none;
                    display: flex;
                    color: var(--comp--text--secondary, #888);
                }
            }

            .renz-privacy-details-close {
                display: none;
                margin-left: auto;
                flex: none;
                width: 2.5rem;
                height: 2.5rem;
                align-items: center;
                justify-content: center;
                color: var(--on-surface, inherit);
                background: transparent;
                border: none;
                border-radius: 50%;
                cursor: pointer;
                text-decoration: none;

                &:focus-visible {
                    outline: 3px solid var(--primary);
                    outline-offset: 2px;
                }
            }

            &[open] .renz-privacy-details-close {
                display: flex;
            }

            .renz-privacy-details-body {
                padding: 0 0.75rem 0.75rem;
                line-height: 1.5;
                // Standard (gestapelt): kein eigener Scroller — der Dialog scrollt als Ganzes.
                max-height: none;
                overflow: visible;
                border-top: 1px solid var(--border-color-default, #e0e0e0);

                p {
                    margin: 0 0 0.5rem 0;
                }

                // Lange URLs/Wörter umbrechen, Medien einpassen: kein Horizontal-Scroll.
                overflow-wrap: anywhere;

                img,
                video,
                iframe {
                    max-width: 100%;
                    height: auto;
                }

                table {
                    display: block;
                    max-width: 100%;
                    overflow-x: auto;
                }

                // Nur der Content: keine Sidebar-Navi, keine Link-Navigation im Panel.
                .content-sidebar,
                nav {
                    display: none;
                }
            }
        }

        .renz-privacy-details[open] summary .renz-privacy-details-chevron {
            // Offen übernimmt der Schließen-Button — Pfeil nur geschlossen zeigen.
            display: none;
        }
    }
</style>
